
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';
import { useToast } from '@/hooks/use-toast';

interface CompanySettings {
  showPrices: boolean;
}

export const useCompanySettings = () => {
  const { currentCompany, userRole } = useCompany();
  const { toast } = useToast();
  const [settings, setSettings] = useState<CompanySettings>({ showPrices: true });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentCompany) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('company_settings')
          .select('show_prices')
          .eq('company_id', currentCompany.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setSettings({ showPrices: !!data.show_prices });
        } else {
          // No record yet => default true
          setSettings({ showPrices: true });
        }
      } catch (err) {
        console.error('Failed to load company settings:', err);
        // Fall back to default: show prices = true
        setSettings({ showPrices: true });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [currentCompany?.id]);

  const setShowPrices = async (value: boolean) => {
    if (!currentCompany) return;
    setSaving(true);
    try {
      // Upsert to ensure a row exists for this company (admins only per RLS)
      const { error } = await supabase
        .from('company_settings')
        .upsert(
          {
            company_id: currentCompany.id,
            show_prices: value,
          },
          { onConflict: 'company_id' }
        );

      if (error) throw error;

      setSettings((prev) => ({ ...prev, showPrices: value }));
      toast({
        title: value ? 'Prix affichés' : 'Prix masqués',
        description: 'Préférence enregistrée pour toute l’équipe.',
      });
    } catch (err) {
      console.error('Failed to save company settings:', err);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Impossible d'enregistrer la préférence.",
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    showPrices: settings.showPrices,
    setShowPrices,
    loading,
    saving,
    isAdmin: userRole === 'admin',
  };
};
